/* eslint-disable @typescript-eslint/no-base-to-string, @typescript-eslint/strict-boolean-expressions */
import React, {useCallback, useMemo, useRef, useState} from "react";
import {
    useReactTable,
    getCoreRowModel,
    // getSortedRowModel,
    // type SortingState,
    type ColumnOrderState,
    type ColumnSizingState,
    type RowSelectionState,
    type VisibilityState,
    type ColumnPinningState,
} from "@tanstack/react-table";
import type {TableConfig} from "~/components/table/table.types";
import {buildTanstackColumns, clsx, createSkeletonRows, isSkeletonRow} from "~/components/table/tableUtils.tsx";
import "~/components/table/table.css";
import {TableHeaderCell} from "~/components/table/TableHeaderCell";
import {TableDataCell} from "~/components/table/TableDataCell";

export interface GenericTableProps<TRow> {
    data: TRow[];
    config: TableConfig<TRow>;
    className?: string;
}

export function GenericTable<TRow extends object>({data, config, className}: GenericTableProps<TRow>) {
    // Column sort is handled by ottopilotDataViewer (API sort), not client-side table headers.
    // const [internalSorting, setInternalSorting] = useState<SortingState>([]);
    const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>([]);
    const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>({});
    const [uncontrolledRowSelection, setUncontrolledRowSelection] = useState<RowSelectionState>({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(config.columnVisibility ?? {});
    const [dragSourceId, setDragSourceId] = useState<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    /** Set synchronously in drag start so drop (same frame) works before React re-renders. */
    const dragSourceColumnIdRef = useRef<string | null>(null);

    const resolvedColumnPinning = useMemo<ColumnPinningState>(() => {
        const ordered = config.columnOrder ?? config.columns.map((column) => column.id);
        const leftPinnedFromConfig = ordered.filter((id) =>
            config.columns.some((column) => column.id === id && column.pinned === "left"),
        );
        const rightPinnedFromConfig = ordered.filter((id) =>
            config.columns.some((column) => column.id === id && column.pinned === "right"),
        );
        const maxPinned =
            config.mobileUi?.isMobile === true ? Math.max(0, config.mobileUi.maxPinnedLeftColumns ?? 1) : null;
        const left = [
            ...(config.rowSelection === "multiple" ? ["__checkbox__"] : []),
            ...(maxPinned === null ? leftPinnedFromConfig : leftPinnedFromConfig.slice(0, maxPinned)),
        ];
        const hasActionsCallbacks =
            config.rowActionsConfig !== undefined ||
            config.onViewRow !== undefined ||
            config.onEditRow !== undefined ||
            config.onDeleteRow !== undefined ||
            config.renderInlineActions !== undefined ||
            (config.rowActions?.length ?? 0) > 0;
        const right = [
            ...rightPinnedFromConfig,
            ...(hasActionsCallbacks && !rightPinnedFromConfig.includes("__actions__") ? ["__actions__"] : []),
        ];
        return {left, right};
    }, [
        config.columns,
        config.columnOrder,
        config.mobileUi?.isMobile,
        config.mobileUi?.maxPinnedLeftColumns,
        config.rowSelection,
        config.rowActionsConfig,
        config.onViewRow,
        config.onEditRow,
        config.onDeleteRow,
        config.renderInlineActions,
        config.rowActions,
    ]);

    const columns = React.useMemo(
        () => buildTanstackColumns(config),
        [
            config,
            config.columns,
            config.rowSelection,
            config.onViewRow,
            config.onEditRow,
            config.onDeleteRow,
            config.rowActions,
            config.rowActionsConfig,
            config.renderInlineActions,
            // config.sorting,
            config.columnResizing,
        ],
    );

    const rowData = useMemo(() => {
        if (config.isLoading && data.length === 0) {return createSkeletonRows<TRow>(config.skeletonRowCount ?? 10);}
        return data;
    }, [config.isLoading, config.skeletonRowCount, data]);

    const table = useReactTable<TRow>({
        data: rowData,
        columns,
        getRowId: (row, index) => {
            if (isSkeletonRow(row)) {
                const skeletonIndex = (row as Record<string, unknown>)["__skeletonIndex__"];
                return `__skeleton__${String(typeof skeletonIndex === "number" ? skeletonIndex : index)}`;
            }
            return String((row as Record<string, unknown>)[config.rowKey as string] ?? Math.random());
        },
        state: {
            // sorting: internalSorting,
            columnOrder: config.columnOrder ?? (internalColumnOrder.length > 0 ? internalColumnOrder : undefined),
            columnSizing: config.columnSizing ?? internalColumnSizing,
            rowSelection: config.rowSelectionState !== undefined ? config.rowSelectionState : uncontrolledRowSelection,
            columnVisibility: config.columnVisibility ?? columnVisibility,
            columnPinning: resolvedColumnPinning,
        },
        // onSortingChange: (updater) => {
        //     const next = typeof updater === "function" ? updater(internalSorting) : updater;
        //     setInternalSorting(next);
        //     config.onSortChange?.(next);
        // },
        onColumnOrderChange: (updater) => {
            setInternalColumnOrder((prev) => {
                const next = typeof updater === "function" ? updater(prev) : updater;
                const normalizedOrder = normalizeSpecialColumns(next);
                const finalOrder = config.keepPinnedLeftFirst
                    ? movePinnedLeftBeforeOthers(normalizedOrder, config.columns)
                    : normalizedOrder;
                config.onColumnOrderChange?.(finalOrder);
                return finalOrder;
            });
        },
        onColumnSizingChange: (updater) => {
            const next = typeof updater === "function" ? updater(internalColumnSizing) : updater;
            setInternalColumnSizing(next);
            config.onColumnSizingChange?.(next);
        },
        onRowSelectionChange: (updater) => {
            const current =
                config.rowSelectionState !== undefined ? config.rowSelectionState : uncontrolledRowSelection;
            const next = typeof updater === "function" ? updater(current) : updater;
            if (config.rowSelectionState === undefined) {
                setUncontrolledRowSelection(next);
            }
            if (config.onRowSelectionChange) {
                const selectedRowIds = Object.keys(next).filter((id) => next[id]);
                const selectedRows = selectedRowIds
                    .map((id) =>
                        rowData.find((r) => String((r as Record<string, unknown>)[config.rowKey as string]) === id),
                    )
                    .filter(Boolean) as TRow[];
                config.onRowSelectionChange({selectedRowIds, selectedRows});
            }
        },
        onColumnVisibilityChange: (updater) => {
            const next = typeof updater === "function" ? updater(columnVisibility) : updater;
            setColumnVisibility(next);
            config.onColumnVisibilityChange?.(next);
        },
        getCoreRowModel: getCoreRowModel(),
        // getSortedRowModel: getSortedRowModel(),
        enableRowSelection: config.rowSelection !== "none" && config.rowSelection !== undefined,
        enableMultiRowSelection: config.rowSelection === "multiple",
        columnResizeMode: "onChange",
        enableColumnResizing: config.columnResizing !== false,
        defaultColumn: {minSize: 60, size: 150},
    });

    const handleDrop = useCallback(
        (e: React.DragEvent, targetId: string) => {
            e.preventDefault();
            e.stopPropagation();
            const sourceId = (e.dataTransfer?.getData("text/plain") || "").trim() || dragSourceColumnIdRef.current;
            dragSourceColumnIdRef.current = null;
            if (!sourceId || sourceId === targetId) {
                setDragOverId(null);
                return;
            }
            const colOrder = table.getState().columnOrder;
            const currentOrder =
                colOrder != null && colOrder.length > 0 ? colOrder : table.getAllLeafColumns().map((c) => c.id);
            const from = currentOrder.indexOf(sourceId);
            const to = currentOrder.indexOf(targetId);
            if (from === -1 || to === -1) {
                setDragOverId(null);
                setDragSourceId(null);
                return;
            }
            if (from === to) {
                setDragOverId(null);
                setDragSourceId(null);
                return;
            }
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            if (moved === undefined) {
                setDragOverId(null);
                setDragSourceId(null);
                return;
            }
            next.splice(to, 0, moved);
            const normalizedOrder = normalizeSpecialColumns(next);
            const finalOrder = config.keepPinnedLeftFirst
                ? movePinnedLeftBeforeOthers(normalizedOrder, config.columns)
                : normalizedOrder;
            setInternalColumnOrder(finalOrder);
            config.onColumnOrderChange?.(finalOrder);
            setDragOverId(null);
            setDragSourceId(null);
        },
        [table, config],
    );

    return (
        <div
            className={clsx("table-container", className)}
            style={{maxHeight: config.maxHeight}}
        >
            <div className="table-scroll-wrapper">
                <table
                    className="table-root"
                    style={{width: table.getTotalSize(), maxWidth: "100%"}}
                >
                    <thead className="table-head">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className="table-head-row"
                            >
                                {headerGroup.headers.map((header) => (
                                    <TableHeaderCell
                                        key={header.id}
                                        header={header}
                                        isDragging={dragSourceId === header.column.id}
                                        dragOverId={dragOverId}
                                        onDragStart={(e, columnId) => {
                                            dragSourceColumnIdRef.current = columnId;
                                            setDragSourceId(columnId);
                                            e.dataTransfer.setData("text/plain", columnId);
                                            e.dataTransfer.effectAllowed = "move";
                                        }}
                                        onDragEnter={(e) => {
                                            e.preventDefault();
                                        }}
                                        onDragOver={(e, columnId) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.dataTransfer.dropEffect = "move";
                                            setDragOverId(columnId);
                                        }}
                                        onDrop={handleDrop}
                                        onDragEnd={() => {
                                            dragSourceColumnIdRef.current = null;
                                            setDragSourceId(null);
                                            setDragOverId(null);
                                        }}
                                        canReorder={config.columnReordering !== false}
                                    />
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="table-body">
                        {table.getRowModel().rows.length === 0 && !config.isLoading ? (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="table-empty-cell"
                                >
                                    {config.emptyState ?? <div className="table-empty-default">No data available</div>}
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row,index) => {
                                const rowIsSkeleton = isSkeletonRow(row.original);
                                const customClass =
                                    !rowIsSkeleton && config.rowClassName ? config.rowClassName(row.original) : "";
                                return (
                                    <tr
                                        key={`${row.id}-${index}`}
                                        className={clsx(
                                            "table-row",
                                            config.rowHover !== false && "table-row--hoverable",
                                            row.getIsSelected() && "table-row--selected",
                                            rowIsSkeleton && "table-row--skeleton",
                                            customClass,
                                        )}
                                        onClick={() => {
                                            if (rowIsSkeleton) {
                                                return;
                                            }
                                            config.onRowClick?.(row.original);
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableDataCell
                                                key={cell.id}
                                                cell={cell}
                                                row={row.original}
                                                config={config}
                                                isSkeletonRow={rowIsSkeleton}
                                            />
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function normalizeSpecialColumns(order: string[]): string[] {
    const withoutCheckbox = order.filter((id) => id !== "__checkbox__");
    const withoutActions = withoutCheckbox.filter((id) => id !== "__actions__");
    const normalizedOrder = [...withoutActions];

    if (order.includes("__checkbox__")) {
        normalizedOrder.unshift("__checkbox__");
    }

    if (order.includes("__actions__")) {
        normalizedOrder.push("__actions__");
    }

    return normalizedOrder;
}

function movePinnedLeftBeforeOthers<TRow extends object>(
    order: string[],
    columns: TableConfig<TRow>["columns"],
): string[] {
    const pinnedLeftIds = new Set(columns.filter((column) => column.pinned === "left").map((column) => column.id));
    const left = order.filter((id) => pinnedLeftIds.has(id) && id !== "__checkbox__" && id !== "__actions__");
    const middle = order.filter((id) => !pinnedLeftIds.has(id) && id !== "__checkbox__" && id !== "__actions__");
    const special = order.filter((id) => id === "__checkbox__" || id === "__actions__");
    return normalizeSpecialColumns([...left, ...middle, ...special]);
}
